import { useDrag } from '@use-gesture/react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';

import { PROMOTIONS, type Promotion } from '@/constants/promotions';

import { usePromotionEligibility } from '@/hooks/usePromotionEligibility';

import { buttons } from '@/styles/buttons';
import { fontStyles } from '@/styles/typography';

import { XIcon } from '@/ui/Icons/XIcon';

import { classNames } from '@/utils/classnames';
import { routes } from '@/utils/routes';

import animations from './animations.module.css';

const PROMO_TIMEOUT_MS = 10_000;
const SWIPE_THRESHOLD_PX = 50;

const PROMO_CAROUSEL_OFFSET_VAR = '--promo-carousel-offset';

export const PromoCarousel = () => {
  const { pathname } = useRouter();

  const [eligiblePromos, setEligiblePromos] = useState<Promotion[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const hasDynamicPromos = PROMOTIONS.some(
    ({ enabled }) => typeof enabled !== 'boolean' && enabled.type === 'wallet-allowlist',
  );
  const { data: promotionEligibility } = usePromotionEligibility({
    enabled: hasDynamicPromos && pathname !== routes.TRADE,
  });

  const activePromo = eligiblePromos[activeIndex];
  const promoCarouselRef = useRef<HTMLElement>(null);
  const previousPromoIdRef = useRef(activePromo?.id);
  const hasMultiplePromos = eligiblePromos.length > 1;
  const isPromoVisible = activePromo != null && pathname !== routes.TRADE;

  const bindSwipe = useDrag(
    ({ event, last, movement: [movementX, movementY] }) => {
      const isTouchPointer = 'pointerType' in event && event.pointerType === 'touch';
      const isHorizontalSwipe =
        Math.abs(movementX) >= SWIPE_THRESHOLD_PX && Math.abs(movementX) > Math.abs(movementY);

      if (!last || !isTouchPointer || event.type === 'pointercancel' || !isHorizontalSwipe) return;

      setActiveIndex((currentIndex) => {
        const direction = movementX < 0 ? 1 : -1;
        return (currentIndex + direction + eligiblePromos.length) % eligiblePromos.length;
      });
    },
    { enabled: hasMultiplePromos, filterTaps: true, pointer: { keys: false } },
  );

  const hidePromo = (cardId: string) => {
    const dismissedIndex = eligiblePromos.findIndex(({ id }) => id === cardId);
    const card = eligiblePromos[dismissedIndex];

    if (card == null) return;

    const { storageKey } = card;
    const visiblePromos = eligiblePromos.filter(({ id }) => id !== cardId);

    localStorage.setItem(storageKey, 'true');
    setEligiblePromos(visiblePromos);
    setActiveIndex((currentIndex) => {
      if (visiblePromos.length === 0 || currentIndex >= visiblePromos.length) return 0;
      return dismissedIndex < currentIndex ? currentIndex - 1 : currentIndex;
    });
  };

  useEffect(() => {
    const visiblePromos = PROMOTIONS.filter(({ enabled, id, storageKey }) => {
      const isStaticallyEnabled = enabled === true;
      const isWalletEligible =
        typeof enabled !== 'boolean' &&
        enabled.type === 'wallet-allowlist' &&
        promotionEligibility?.eligiblePromotionIds.includes(id) === true;

      const isDismissed = localStorage.getItem(storageKey) === 'true';

      return (isStaticallyEnabled || isWalletEligible) && !isDismissed;
    });

    setEligiblePromos(visiblePromos);
    setActiveIndex((currentIndex) => (currentIndex < visiblePromos.length ? currentIndex : 0));
  }, [promotionEligibility?.eligiblePromotionIds]);

  useEffect(() => {
    if (!hasMultiplePromos || pathname === routes.TRADE) return;

    const advanceTimer = setTimeout(() => {
      setActiveIndex((currentIndex) => (currentIndex + 1) % eligiblePromos.length);
    }, PROMO_TIMEOUT_MS);

    return () => clearTimeout(advanceTimer);
  }, [activeIndex, eligiblePromos.length, hasMultiplePromos, pathname]);

  useEffect(() => {
    previousPromoIdRef.current = activePromo?.id;
  }, [activePromo?.id]);

  useEffect(() => {
    const { style: rootStyle } = document.documentElement;
    const promoCarousel = promoCarouselRef.current;

    if (!isPromoVisible || promoCarousel == null) {
      rootStyle.removeProperty(PROMO_CAROUSEL_OFFSET_VAR);
      return;
    }

    const promoCarouselHeight = Math.ceil(promoCarousel.getBoundingClientRect().height);
    rootStyle.setProperty(
      PROMO_CAROUSEL_OFFSET_VAR,
      `calc(${promoCarouselHeight}px + var(--app-toaster-gap))`,
    );

    return () => {
      rootStyle.removeProperty(PROMO_CAROUSEL_OFFSET_VAR);
    };
  }, [activePromo?.id, isPromoVisible]);

  if (!isPromoVisible) {
    return null;
  }

  const { cta, description, id, image, title } = activePromo;
  const ctaClassNames = buttons({ size: 'small', color: 'black', shape: 'pill' });

  // Ensure opacity animation only runs after initial render
  const shouldAnimatePromo =
    previousPromoIdRef.current != null && previousPromoIdRef.current !== activePromo?.id;
  const fadeInStyles = shouldAnimatePromo ? animations['animate-promo-fade-in'] : undefined;

  return (
    <section
      aria-label="Matcha promotions"
      className={classNames(
        'flex z-50 right-4 w-[calc(100%-32px)] tablet:right-10 tablet:w-90',
        'fixed tablet:bottom-[calc(24px+var(--toast-stack-height,0px))]',
        'transition-[bottom] duration-150',
        'bottom-[calc(12px+var(--toast-stack-height,0px))]',
      )}
      ref={promoCarouselRef}
    >
      <div
        {...bindSwipe()}
        aria-label="Matcha promotion"
        className="flex grow-1 flex-row justify-between gap-3 rounded-lg border border-token-alphalight-300 bg-special-background-modal p-3 pb-0 shadow-15"
        style={{ touchAction: 'pan-y' }}
      >
        <div className="flex flex-col justify-between">
          <div className={classNames('flex flex-col gap-3', fadeInStyles)} key={id}>
            <div className="flex flex-col gap-1 px-1">
              <p className={fontStyles({ variant: 'H6' })}>{title}</p>
              <p className={fontStyles({ variant: 'BODY_SM', class: 'text-token-gray-900' })}>
                {description}
              </p>
            </div>
            {typeof cta.href === 'string' ? (
              <a
                className={ctaClassNames}
                href={cta.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {cta.label}
              </a>
            ) : (
              <Link className={ctaClassNames} href={cta.href}>
                {cta.label}
              </Link>
            )}
          </div>
          {hasMultiplePromos && (
            <div className="flex">
              {eligiblePromos.map(({ id: promoId }, index) => (
                <button
                  aria-label={`Show carousel item ${index + 1}`}
                  aria-pressed={index === activeIndex}
                  className="group flex cursor-pointer items-center justify-center rounded-full px-1 py-2 focus-visible:outline-token-gray-500 focus-visible:outline-[3px]"
                  key={promoId}
                  onClick={() => setActiveIndex(index)}
                  type="button"
                >
                  <span
                    className={classNames(
                      'relative h-2 w-8 overflow-hidden rounded-full shadow-10 transition-[width,background-color] duration-200 ease-out',
                      { 'bg-token-gray-400': index === activeIndex },
                      { 'bg-token-gray-400 group-hover:bg-token-gray-500': index !== activeIndex },
                    )}
                  >
                    {index === activeIndex ? (
                      <span
                        className={`absolute inset-0 origin-left rounded-full bg-token-gray-900 ${animations['animate-promo-dot-fill']}`}
                        style={{ animationDuration: `${PROMO_TIMEOUT_MS}ms` }}
                      />
                    ) : null}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="relative mb-3 flex size-36 shrink-0 items-center justify-center overflow-hidden rounded-md bg-token-alphalight-100">
          <div className={classNames('relative size-full', fadeInStyles)} key={id}>
            <Image
              alt={image.alt}
              className="object-cover"
              fill={true}
              sizes="144px"
              src={image.src}
              fetchPriority="high"
            />
          </div>
          <button
            aria-label="Dismiss promotion"
            className={classNames(
              'absolute right-1 top-1 z-10 flex size-6 cursor-pointer items-center justify-center',
              'text-token-gray-800 hover:text-token-gray-700 bg-special-background-modal rounded-full',
              'focus-visible:outline-token-gray-500 focus-visible:outline-[3px]',
            )}
            onClick={() => hidePromo(id)}
            type="button"
          >
            <XIcon className="size-3.5" />
          </button>
        </div>
      </div>
    </section>
  );
};
